import { LitElement, html, TemplateResult, PropertyValues } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { IconSets } from '../../../../theme';
import { FontSize } from '../../../../ui/gzIconButton/types';
import { DEVICES_CLASS, DEVICES_TYPES_ORDER } from '../../../common/constants';
import { BaseWebComponentTranslator } from '../../../../framework/BaseWebComponentTranslator';
import { USERS_ICON_CLASS_MAP, USERS_TYPES_ORDER } from '../../../userBehaviorRisksApp/types';
import { WEB_COMPONENT_GZ_RM_ICONS_LIST } from '../../../../webcomponents.constants';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-icons-list
 */
@customElement(WEB_COMPONENT_GZ_RM_ICONS_LIST)
export class GzRmIconsListWebComponent extends LitElement {
  /**
   * Items will be displayed as a series of icons and numbers
   * next to each other.
   */
  @property({ type: Object, attribute: true })
  items: { type: number; count: number }[] = [];

  /**
   * Icon class map type. Decides which mapping for type -> icon name to use and which ordering.
   */
  @property({ type: String, attribute: true, reflect: true })
  iconClassMapType = '';

  /**
   * Which icon set to use to render the icons. Undefined means the default one.
   */
  @property({ type: String, attribute: true, reflect: true })
  iconSet?: keyof typeof IconSets = undefined;

  @state()
  iconClassState: IconClassState = {
    order: [],
    mapping: new Map(),
    translateFn: () => '',
  };

  static get styles() {
    return styles;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);

    if (this.iconClassMapType) {
      const classMapStates = iconClassTypes[this.iconClassMapType];
      if (classMapStates) {
        this.iconClassState = classMapStates;
      }
    }
  }

  render(): TemplateResult {
    if (this.items.length === 0) {
      return html`
        <gz-icon-label
          label="--"
          icon="ds-icon-bullet"
          .size="${FontSize.ICON_SIZE_20}"
          iconColor="var(--grey-layout-202-disabled-icons)"
        ></gz-icon-label>`;
    }

    const typesToRender = Object.fromEntries(this.items.map(item => [item.type, item]));
    const tooltipText: string[] = [];
    const iconsToRender: TemplateResult[] = [];

    for (const type of this.iconClassState.order) {
      const toRender = typesToRender[type];
      if (!toRender) {
        continue;
      }

      const [iconHtml, iconTooltip] = this.renderIcon(toRender.type, toRender.count);

      iconsToRender.push(iconHtml);
      tooltipText.push(iconTooltip);
    }

    return html` <gz-tooltip .tooltipText="${tooltipText.join(', ')}" isEllipsis> ${iconsToRender}</gz-tooltip>`;
  }

  private renderIcon(type: number, count: number): [TemplateResult, string] {
    const label = count < 100 ? count : '99+';

    return [
      html`
        <gz-icon-label
          exportparts="highlightable"
          .customIconSet="${this.iconSet}"
          .label="${label}"
          .icon="${this.iconClassState.mapping.get(type)}"
          .size="${FontSize.ICON_SIZE_20}"
          iconColor="var(--chart-302-icon-buttons-titles)"
        ></gz-icon-label>`,
      `${this.iconClassState.translateFn(type)}: ${count}`,
    ];
  }
}

interface IconClassState {
  order: number[];
  mapping: Map<number, string>;
  translateFn: (type: number) => string;
}

type IconClassTypes = Record<string, IconClassState>;

const iconClassTypes: IconClassTypes = {
  devices: {
    mapping: DEVICES_CLASS,
    order: DEVICES_TYPES_ORDER,
    translateFn: BaseWebComponentTranslator.getInstance().getDeviceTypes,
  },
  users: {
    mapping: USERS_ICON_CLASS_MAP,
    order: USERS_TYPES_ORDER,
    translateFn: BaseWebComponentTranslator.getInstance().getRiskManagementUserType,
  }
};

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-icons-list': GzRmIconsListWebComponent;
  }
}
